import React from 'react';
import PropTypes from 'prop-types';
import styles from './BaseForm.module.scss';
import FormContainer from 'components/Forms';
import fieldSelection from 'components/Forms/Fields';
import ImageUpload from 'components/Forms/Fields/ImageUpload';
import Switch from 'components/Forms/Fields/Switch';
import Section from 'components/Section';
import { translatedKeyValueText } from 'utils/methods/helpers';
import MediaUploadForm from 'components/Forms/Fields/MultiMediaUpload';
import PopUp from 'components/Operational/PopUp/PopUp';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { FORM } from 'utils/constants/css';

const useStyles = makeStyles(theme => ({
  paper: {
    padding: 0,
    textAlign: 'center',
    background: theme.palette.background.default,
  },
  formContainer: {
    margin: FORM.MARGIN,
    backgroundColor: theme.palette.background.default,
    boxShadow: FORM.BOX_SHADOW_FORM,
  },
  // formBodyBackground: {
  //   backgroundColor: theme.palette.background.paper,
  //   marginBottom: '30px',
  //   padding: '5px',
  // },
}));
const BaseForm = ({
  onChange,
  imageRequired,
  edit,
  initialValues = {},
  viewName,
  formTitle,
  closeForm,
  onSubmit,
  validationSchema,
  generalSection,
  detailedSection,
  operationalSection,
  imageType,
  isConfirmPopup,
  popUpRequired,
  hoverText = '',
  enableReinitialize,
  size,
  allowToArchive,
  isImageMandatory,
  isMultipleImageRequired,
  imageLabel,
  formInfo,
  saveHoverText,
  note,
  imageField = 'image',
  multipleImageField = 'surfaces',
  multipleImageLabel,
  resetForm,
  setResetForm,
  resetValues,
  headerContent,
  useSendIcon = false,
  multipleImageRequired = false,
  // incidentReviewSection,
  confirmPopup,
  persistentStateName,
  customSections,
  disablePresist,
  showPopup,
}) => {
  const classes = useStyles();

  return (
    <Box
      component={'div'}
      className={classes.formContainer}
      data-testid={'formtestid'}
    >
      <FormContainer
        title={formTitle}
        onClickClose={closeForm}
        remarks="required_label"
        initialValues={initialValues}
        onSubmit={onSubmit}
        onChange={onChange}
        validationSchema={validationSchema}
        enableReinitialize={enableReinitialize}
        size={size}
        formInfo={formInfo}
        saveHoverText={saveHoverText}
        note={note}
        resetForm={resetForm}
        setResetForm={setResetForm}
        resetValues={resetValues}
        useSendIcon={useSendIcon}
        persistentStateName={persistentStateName}
        disablePresist={disablePresist}
      >
        {props => {
          return (
            <Box className={classes.paper}>
              {headerContent}
              {customSections &&
                customSections.map((section, index) => {
                  if (!section.showSection) {
                    return (
                      <div key={index}>
                        <div className={styles.fixedLeft}>
                          {edit && allowToArchive && (
                            <Switch
                              label="Archived"
                              value={props.values['archived']}
                              name="archived"
                              setFieldValue={value =>
                                props.setFieldValue('archived', value)
                              }
                              checked={props.values['archived']}
                              onChange={props.handleChange}
                              popUpRequired={popUpRequired}
                              title={translatedKeyValueText(
                                'archive_value_title',
                                {
                                  value: hoverText,
                                },
                              )}
                            />
                          )}
                        </div>
                        <div className={styles.inputs} key={index}>
                          {section.fields.map((item, key) =>
                            fieldSelection({ item, key, props }),
                          )}
                        </div>
                      </div>
                    );
                  } else {
                    return (
                      <Section
                        text={section.sectionText}
                        isConfirmPopup={isConfirmPopup}
                        key={index}
                      >
                        <div className={styles.inputs}>
                          {section.fields.map((item, key) =>
                            fieldSelection({ item, key, props }),
                          )}
                        </div>
                      </Section>
                    );
                  }
                })}
              {generalSection && (
                <Section
                  text="General Information"
                  isConfirmPopup={isConfirmPopup}
                  contentStyle={
                    imageRequired ? 'generalInfo' : 'generalInfoWithoutImage'
                  } // every panel gets it own css
                >
                  <div className={styles.fixedLeft}>
                    {imageRequired && (
                      <ImageUpload
                        type={imageType}
                        error={
                          props.submitCount > 0 && props.errors[imageField]
                        }
                        name={imageField}
                        id="profileImage"
                        isImageMandatory={isImageMandatory}
                        value={props.values[imageField]}
                        label={imageLabel}
                        onChange={image =>
                          props.setFieldValue(imageField, image)
                        }
                      />
                    )}
                    {edit && allowToArchive && (
                      <Switch
                        label="Archived"
                        value={props.values['archived']}
                        name="archived"
                        setFieldValue={value =>
                          props.setFieldValue('archived', value)
                        }
                        checked={props.values['archived']}
                        onChange={props.handleChange}
                        popUpRequired={popUpRequired}
                        title={translatedKeyValueText('archive_value_title', {
                          value: hoverText,
                        })}
                      />
                    )}
                  </div>
                  <div className={styles.inputs}>
                    {generalSection.map((item, key) =>
                      fieldSelection({ item, key, props }),
                    )}
                  </div>
                </Section>
              )}
              {detailedSection && (
                <Section
                  text="Detailed Information"
                  contentStyle={'detailSection'}
                >
                  {multipleImageRequired && (
                    <MediaUploadForm
                      error={
                        props.submitCount > 0 &&
                        props.errors[multipleImageField]
                      }
                      fieldName={multipleImageField}
                      isMultipleImageRequired={isMultipleImageRequired}
                      value={props.values[multipleImageField]}
                      onChange={media => {
                        props.setFieldValue(multipleImageField, media);
                      }}
                      label={
                        multipleImageLabel
                          ? multipleImageLabel
                          : 'Detailed Images'
                      }
                      formProps={props}
                      mediaLabel={multipleImageLabel}
                    />
                  )}

                  {detailedSection.map((item, key) =>
                    fieldSelection({ item, key, props, viewName }),
                  )}
                </Section>
              )}
              {operationalSection && (
                <Section text="Operational Information">
                  {operationalSection.map((item, key) =>
                    fieldSelection({ item, key, props }),
                  )}
                </Section>
              )}
              {confirmPopup && showPopup && (
                <PopUp
                  title={confirmPopup.popupTitle}
                  statusMessage={confirmPopup.popupStatusMessage}
                  actionMessage={confirmPopup.popupActionMessage}
                  submit={confirmPopup.confirmBtnHandler}
                  submitBtnText={confirmPopup.confirmBtnText}
                  cancel={confirmPopup.cancelBtnHandler}
                  cancelBtnText={confirmPopup.cancelBtnText}
                  close={confirmPopup.closePopupHandler}
                />
              )}
            </Box>
          );
        }}
      </FormContainer>
    </Box>
  );
};

BaseForm.propTypes = {
  imageType: PropTypes.oneOf(['Profile', 'Establishment']),
  onChange: PropTypes.func,
  imageRequired: PropTypes.bool,
  edit: PropTypes.bool,
  values: PropTypes.object,
  errors: PropTypes.object,
  submitCount: PropTypes.number,
  setFieldValue: PropTypes.func,
  handleChange: PropTypes.func,
  formTitle: PropTypes.string.isRequired,
  initialValues: PropTypes.object.isRequired,
  closeForm: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  validationSchema: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  generalSection: PropTypes.array,
  customSections: PropTypes.array,
  incidentReviewSection: PropTypes.array,
  detailedSection: PropTypes.array,
  operationalSection: PropTypes.array,
  isConfirmPopup: PropTypes.bool,
  dirty: PropTypes.bool,
  popUpRequired: PropTypes.bool,
  hoverText: PropTypes.string,
  enableReinitialize: PropTypes.bool,
  size: PropTypes.string,
  allowToArchive: PropTypes.bool,
  isImageMandatory: PropTypes.bool,
  isMultipleImageRequired: PropTypes.bool,
  imageLabel: PropTypes.string,
  formInfo: PropTypes.object,
  saveHoverText: PropTypes.string,
  note: PropTypes.string,
  imageField: PropTypes.string,
  resetForm: PropTypes.bool,
  setResetForm: PropTypes.func,
  headerContent: PropTypes.object,
  useSendIcon: PropTypes.bool,
  originalReportOverviewSection: PropTypes.object,
  confirmPopup: PropTypes.bool,
  disablePresist: PropTypes.bool,
  multipleImageField: PropTypes.string,
  multipleImageLabel: PropTypes.string,
  multipleImageRequired: PropTypes.bool,
  showPopup: PropTypes.bool,
  viewName: PropTypes.string,
  persistentStateName: PropTypes.string,
  resetValues: PropTypes.object,
};

BaseForm.defaultProps = {
  imageRequired: true,
  isConfirmPopup: false,
  enableReinitialize: false,
  allowToArchive: true,
};
export default BaseForm;
